import { intersection } from 'lodash-es';
import { NavigationGuardNext, Route } from 'vue-router';

import { eventBus } from '@/eventbus/eventbus';
import { SET_PREVIOUS_URL } from '@/store/action-types';
import { parseJwt } from '@/bundles/App/helpers/parseJWT';
import { RoleNameEnum } from '@/bundles/Roles/types';

const handlingAccountIdRoutes = (to, store) => {
  if (isNaN(to.params.accountId)) {
    const accountId = store?.state?.activeAccount?.id;

    return `/${accountId}${to.fullPath}`;
  }

  return '';
};

export const redirectHandler = (to, next, store, isRedirectTo404 = false) => {
  const route = handlingAccountIdRoutes(to, store);

  if (route) {
    next(route);
    return;
  }

  isRedirectTo404 ? next({ name: '404' }) : next();
};

export const requiresTokenAbsenceGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  if (store?.state?.idToken) {
    const accountId = store?.state?.activeAccount?.id ?? store?.state?.user?.active_account?.id;
    next({ name: 'dashboard', params: { accountId } });
    return false;
  }

  return true;
};

export const requiresTokenOrTemporaryTokenGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  if (!store?.state?.idToken && !store?.state?.temporaryAuthToken) {
    next('/signin');
    return false;
  }

  return true;
};

export const requiresTokenGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  if (!store?.state?.idToken) {
    // History-mode router: use the relative target path (incl. query) so the
    // post-login `router.push(prevUrl)` resolves to a real route instead of
    // falling through to /404. `to.fullPath` = e.g. /oauth/authorize?client_id=…
    store.commit(SET_PREVIOUS_URL, to.fullPath || '/dashboard');

    if (from.name && from.name !== 'signin') {
      eventBus.$emit('showSignIn', { preventRedirect: true })
    } else {
      next('/signin')
    }

    return false;
  }

  store.commit(SET_PREVIOUS_URL, null);
  return true;
};

export const requiresRedirectGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  redirectHandler(to, next, store);
  return false;
};

export const requiresAdminTokenGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  if (!store.state.adminToken || new Date().getTime() > store.state.adminTokenExpiresTime || to.query.showLogin) {
    next(`${to.path}?showLogin=true`);
    return false;
  }

  return true;
};

export const requiresSuperAdminRoleGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresFinancialAdminRoleGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN, RoleNameEnum.FINANCIAL_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresFinancialOrMarketingAdminRoleGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN, RoleNameEnum.FINANCIAL_ADMIN, RoleNameEnum.MARKETING_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresAdminRoleGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.ADMIN, RoleNameEnum.SUPER_ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requiresFinancialAdminOrAdminRoleGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const roles = [RoleNameEnum.SUPER_ADMIN, RoleNameEnum.FINANCIAL_ADMIN, RoleNameEnum.ADMIN];
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];

  if (intersection(roles, userRoles).length) {
    return true;
  }

  next('/access');
  return false;
};

export const requireRegularUser = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const userRoles = parseJwt(store?.state?.idToken)?.scope || [];
  if (!userRoles.includes('client') || (userRoles.includes('client') && to.meta?.allowClientAccess) || to.path.includes('access')) {
    return true;
  }
  next('/access');
  return false;
};

export const requiresPasswordChangeGuard = (to: Route, from: Route, next: NavigationGuardNext, store: any) => {
  const isPasswordTemporary = store?.state?.isPasswordTemporary;
  const accountId = store?.state?.activeAccount?.id;

  if (isPasswordTemporary && to.name !== 'user-security') {
    next({ name: 'user-security', params: { accountId } });
    return false;
  }

  return true;
};
